import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import FranchiseAdminToolbar from "./FranchiseAdminToolbar";
import FranchiseGroupList from "./FranchiseGroupList";
import FacilityFlatList from "./FacilityFlatList";
import {
  normalizeFleet,
  filterFleet,
  countAttention,
  summarizeFleet,
} from "../../utils/franchiseAdminModel";
import {
  listBdms,
  updateFacilityDetails,
  updateFacilityOwnership,
  updateFranchiseSettings,
  updateFranchiseOwnership,
  saveFacilitySite,
  deleteFacilitySite,
} from "../../services/franchiseAdminApi";

// Franchise Configuration — the franchise/facility reference-data surface, one of the two areas
// under Administration (the other is LK/SI Eligibility Administration). Extracted from the former
// FranchiseAdministration container so configuration and eligibility no longer share one component;
// the Administration-level tabs live in AdministrationLayout.
//
// Franchises and Facilities remain two LENSES over one dataset and share this component's toolbar
// and filter state, which is why the lens toggle lives here (a feature concern) rather than in the
// layout. The lens lives in the URL (`/administration/franchise` vs `/administration/franchise/
// facilities`) so each is deep-linkable and the browser Back button works. `comcodes` is the
// UNFILTERED fleet -- the Active-only toggle lives in this page's own toolbar.

export default function FranchiseConfiguration(props) {
  const comcodes = props.comcodes;
  const updateComcodes = props.updateComcodes;
  const comcodesError = props.comcodesError;

  const params = useParams();
  const navigate = useNavigate();
  const activeLens = params.lens === "facilities" ? "facilities" : "franchises";
  const handleLensChange = (event, next) => {
    navigate(next === "facilities" ? "/administration/franchise/facilities" : "/administration/franchise");
  };

  // Active + inactive BDMs for the ownership dialog. Fetched once.
  const [bdmOptions, setBdmOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listBdms()
      .then((data) => {
        if (!cancelled) setBdmOptions(data.bdms || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Save handlers ---------------------------------------------------------------------------
  // Each rejects on failure so the calling dialog can surface the server's message, and refetches
  // on success.

  const handleUpdateFranchiseDetails = async (payload) => {
    const data = await updateFranchiseSettings(payload);
    updateComcodes();
    return data;
  };

  const handleUpdateFranchiseOwnership = async (payload) => {
    const data = await updateFranchiseOwnership(payload);
    updateComcodes();
    return data;
  };

  const handleUpdateFacilityDetails = async (payload) => {
    const data = await updateFacilityDetails(payload);
    updateComcodes();
    return data;
  };

  const handleUpdateFacilityOwnership = async (payload) => {
    const data = await updateFacilityOwnership(payload);
    updateComcodes();
    return data;
  };

  const handleSaveSite = async (payload) => {
    const data = await saveFacilitySite(payload);
    updateComcodes();
    return data;
  };

  const handleDeleteSite = async (facilitySiteId) => {
    const data = await deleteFacilitySite(facilitySiteId);
    updateComcodes();
    return data;
  };

  // ---- Shared filter state (Franchises + Facilities lenses) ------------------------------------
  const [query, setQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [presetIds, setPresetIds] = useState([]);
  const [message, setMessage] = useState(null);

  const fleet = useMemo(() => normalizeFleet(comcodes), [comcodes]);
  const visibleFleet = useMemo(
    () => filterFleet(fleet, { query, activeOnly, presetIds }),
    [fleet, query, activeOnly, presetIds]
  );
  const attentionCounts = useMemo(() => countAttention(fleet, { activeOnly }), [fleet, activeOnly]);
  const fleetSummary = useMemo(() => summarizeFleet(visibleFleet), [visibleFleet]);

  const handleTogglePreset = (presetId) => {
    setPresetIds((current) =>
      current.includes(presetId) ? current.filter((id) => id !== presetId) : [...current, presetId]
    );
  };

  const handleClearFilters = () => {
    setQuery("");
    setPresetIds([]);
  };

  const hasFilters = query.trim() !== "" || presetIds.length > 0;

  // Every action the lenses can offer is wired here. An omission is INVISIBLE rather than an
  // error -- each action renders only when its handler is supplied -- so FranchiseConfiguration
  // .test.js asserts the full set is present, which is the only thing that catches a dropped prop.
  const sharedListProps = {
    showActions: true,
    hasFilters,
    bdmOptions,
    onSaveOwnership: handleUpdateFacilityOwnership,
    onSaveDetails: handleUpdateFacilityDetails,
    onSaveFranchiseDetails: handleUpdateFranchiseDetails,
    onSaveFranchiseOwnership: handleUpdateFranchiseOwnership,
    onSaveSite: handleSaveSite,
    onDeleteSite: handleDeleteSite,
    onSaved: setMessage,
  };

  const toolbar = (
    <FranchiseAdminToolbar
      query={query}
      onQueryChange={setQuery}
      activeOnly={activeOnly}
      onActiveOnlyChange={setActiveOnly}
      presetIds={presetIds}
      onTogglePreset={handleTogglePreset}
      onClear={handleClearFilters}
      counts={attentionCounts}
      summary={fleetSummary}
    />
  );

  const scopeNote =
    activeLens === "facilities"
      ? "Every facility fleet-wide, ungrouped — for ownership cleanup and bulk review."
      : "Current LK/SI franchises and their facilities, grouped by franchise.";

  let lensContent;
  if (comcodesError) {
    lensContent = (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        Unable to load franchises. Please try again later.
      </Typography>
    );
  } else if (activeLens === "facilities") {
    lensContent = (
      <>
        {toolbar}
        <FacilityFlatList franchises={visibleFleet} {...sharedListProps} />
      </>
    );
  } else {
    lensContent = (
      <>
        {toolbar}
        <FranchiseGroupList franchises={visibleFleet} {...sharedListProps} />
      </>
    );
  }

  return (
    <Box>
      <Tabs value={activeLens} onChange={handleLensChange} sx={{ borderBottom: 1, borderColor: "divider", mb: 1 }}>
        <Tab label="Franchises" value="franchises" />
        <Tab label="Facilities" value="facilities" />
      </Tabs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {scopeNote}
      </Typography>

      {lensContent}

      <Snackbar
        open={message !== null}
        autoHideDuration={4000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setMessage(null)} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
